import React from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography, Avatar, useMediaQuery } from "@mui/material";
import { Close, PersonAddAltOutlined } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import utils from "../../universal/utils";
import {
    pickAvatarUpdatedAtMs,
    resolveAvatarSrc,
} from "../../utils/avatarUrl";
import Loader from "../common/loader";
import { DIALOG_CANCEL_BUTTON_SX } from "../common/dialogActionButtonSx";
import { LOBBY_PARTY_CARD_SX, MOBILE_NAV_MQ } from "./lobbyPageStyles";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";
import LobbyInviteMobileRow from "./LobbyInviteMobileRow";

const basePath = utils.getEdgeBasePath();

const CANCEL_INVITE_SX = {
    ...DIALOG_CANCEL_BUTTON_SX,
    position: "relative",
    zIndex: 4,
    flex: "0 0 auto",
    width: "calc(100% - 3rem)",
    minWidth: 0,
    height: "1.85rem",
    px: "0.7rem",
    gap: "0.28rem",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: colors.text.primary,
    fontSize: "0.62rem",
    "&:hover:not(:disabled)": {
        color: colors.text.primary,
        background: "rgba(255,255,255,0.12)",
        borderColor: "rgba(255,255,255,0.18)",
    },
};

function CancelInviteButton({
    pendingInvite,
    onCancelInvite,
    iconOnly = false,
    sx,
}) {
    if (!pendingInvite?.userId || typeof onCancelInvite !== "function") {
        return null;
    }

    return (
        <Button
            type="button"
            disableElevation
            disableRipple
            aria-label="Cancel invite"
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onCancelInvite(pendingInvite);
            }}
            onMouseDown={(event) => event.stopPropagation()}
            startIcon={
                iconOnly ? undefined : (
                    <Close sx={{ fontSize: "0.78rem !important" }} />
                )
            }
            sx={{
                ...CANCEL_INVITE_SX,
                ...(iconOnly
                    ? {
                          width: "2rem",
                          minWidth: "2rem",
                          height: "2rem",
                          px: 0,
                          ml: "auto",
                      }
                    : {}),
                "& .MuiButton-startIcon": {
                    mr: 0,
                },
                ...sx,
            }}
        >
            {iconOnly ? (
                <Close sx={{ fontSize: "1rem" }} />
            ) : (
                "Cancel"
            )}
        </Button>
    );
}

CancelInviteButton.propTypes = {
    pendingInvite: PropTypes.object,
    onCancelInvite: PropTypes.func,
    iconOnly: PropTypes.bool,
    sx: PropTypes.object,
};

export default function LobbyInvitePlayersBox({
    pendingInvite = null,
    onCancelInvite,
}) {
    const isMobile = useMediaQuery(MOBILE_NAV_MEDIA);

    const avatarSrc = resolveAvatarSrc({
        basePath,
        picture: pendingInvite?.picture,
        userId: pendingInvite?.userId || pendingInvite?._id,
        avatarUpdatedAtMs: pickAvatarUpdatedAtMs(pendingInvite),
        hasCustomAvatar: !!pendingInvite?.avatar,
    });

    if (isMobile) {
        return (
            <LobbyInviteMobileRow
                pendingInvite={pendingInvite}
                avatarSrc={avatarSrc}
                action={
                    <CancelInviteButton
                        pendingInvite={pendingInvite}
                        onCancelInvite={onCancelInvite}
                        iconOnly
                    />
                }
            />
        );
    }

    return (
        <Box
            sx={{
                ...LOBBY_PARTY_CARD_SX,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                position: 'relative',
                isolation: 'isolate',
                borderRadius: 0,
                background: `
                linear-gradient(
                    90deg,
                    transparent 0%,
                    rgba(232,163,61,0.65) 50%,
                    transparent 100%
                ) 0 0 / 100% 1px no-repeat,
                linear-gradient(
                        180deg,
                        rgba(28,30,32,0.96) 0%,
                        rgba(20,22,25,0.98) 100%
                    )
                `,
                border: `1px solid rgba(255,255,255,0.06)`,
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                cursor: 'pointer',
                transform: 'translateZ(0)',
                transition: `
                    border-color 0.15s ease-in-out,
                    box-shadow 0.15s ease-in-out,
                    background 0.15s ease-in-out
                `,
                boxShadow: `
                    inset 0 1px 0 rgba(255,255,255,0.03),
                    inset 0 -1px 0 rgba(0,0,0,0.3),
                    0 10px 30px rgba(0,0,0,0.28)
                `,
                "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    borderRadius: "inherit",
                    backgroundImage: `
                    repeating-linear-gradient(
                        0deg,
                        rgba(255,255,255,0.025) 0px,
                        rgba(255,255,255,0.025) 1px,
                        transparent 2px,
                        transparent 5px
                    )
                `,

                    opacity: 0.7,
                    pointerEvents: "none",
                    zIndex: 0,
                },
                "&::after": {
                    content: '""',
                    position: "absolute",
                    inset: "-40%",

                    background: `
                    linear-gradient(
                        115deg,
                        transparent 30%,
                        rgba(255,255,255,0.045) 50%,
                        transparent 70%
                    )
                `,
                    transform: "translateX(-120%)",
                    transition: "transform 900ms cubic-bezier(0.22, 1, 0.36, 1)",
                    pointerEvents: "none",
                    zIndex: 2,
                },
                "& .slotBorder": {
                    position: "absolute",
                    inset: 0,
                    borderRadius: "inherit",
                    padding: "1px",
                    background: `
                    linear-gradient(
                        135deg,
                        rgba(246,185,76,0.38),
                        transparent 28%,
                        transparent 72%,
                        rgba(246,185,76,0.14)
                    )
                `,
                    WebkitMask:
                        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    WebkitMaskComposite: "xor",
                    pointerEvents: "none",
                    opacity: 0.9,
                    zIndex: 3,
                },
                "& .slotGlow": {
                    position: "absolute",
                    inset: 0,
                    borderRadius: "inherit",
                    background: `
                    radial-gradient(
                        circle at top,
                        rgba(246,185,76,0.08) 0%,
                        transparent 65%
                    )
                `,
                    opacity: 0.65,
                    pointerEvents: "none",
                    zIndex: 1,
                },
                '&:hover': {
                    borderColor: 'rgba(246,185,76,0.22)',
                    boxShadow: `
                        inset 0 1px 0 rgba(255,255,255,0.04),
                        inset 0 -1px 0 rgba(0,0,0,0.34),
                        0 18px 42px rgba(0,0,0,0.38)
                    `,
                    '&::after': {
                        transform: "translateX(120%)",
                    },
                },
            }}
        >
            <Box className="slotGlow" />
            <Box className="slotBorder" />

            {pendingInvite ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            minHeight: 0,
                        }}
                    >
                        <Loader />
                        <Typography
                            sx={{
                                color: colors.text.secondary,
                                fontSize: { xs: '1rem', xl: '1.3rem' },
                                marginTop: { xs: '0.2rem', xl: '0.4rem' },
                            }}
                        >
                            Inviting
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                padding: '0 1rem',
                                boxSizing: 'border-box',
                                marginTop: '0.4rem',
                                gap: '0.4rem',
                            }}
                        >
                            <Avatar
                                size="large"
                                src={avatarSrc}
                                sx={{
                                    width: { xs: '2rem', xl: '2.8rem' },
                                    height: { xs: '2rem', xl: '2.8rem' },
                                    margin: 0,
                                }}
                            />
                            <Typography
                                sx={{
                                    color: colors.text.primary,
                                    fontSize: { xs: '1rem', xl: '1.3rem' },
                                    fontWeight: 700,
                                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                                    display: 'block',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    minWidth: 0,
                                }}
                            >
                                {pendingInvite.nickname}
                            </Typography>
                        </Box>
                    </Box>
                    <CancelInviteButton
                        pendingInvite={pendingInvite}
                        onCancelInvite={onCancelInvite}
                        sx={{ mb: "1.25rem" }}
                    />
                </Box>
            ) : (
                <>
                    <PersonAddAltOutlined
                        className="addIcon"
                        sx={{
                            fontSize: { xs: '2.15rem', xl: '2.35rem' },
                            color: colors.text.primary,
                            filter: `drop-shadow(0 2px 4px ${alpha(colors.background.dark, 0.45)})`,
                            transition: 'color 0.3s ease',
                        }}
                    />
                    <Typography
                        sx={{
                            color: colors.text.primary,
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: { xs: '0.72rem', xl: '0.78rem' },
                            textTransform: 'uppercase',
                            fontWeight: 500,
                            marginTop: '10px',
                            textAlign: 'center',
                            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                            letterSpacing: '0.08em',
                            transition: 'all 0.3s ease',
                        }}
                    >
                        Empty Slot
                    </Typography>
                </>
            )}
        </Box>
    );
}

LobbyInvitePlayersBox.propTypes = {
    pendingInvite: PropTypes.object,
    onCancelInvite: PropTypes.func,
};
